import { Config } from '@/config';
import apiService from '@/services/api.service';
import type { SectionDto } from '@/services/types/api.types';
import { defineStore } from 'pinia';
import { Constants } from '@/constants';
import { useConsultationsStore } from '@/stores/consultations.store';
import router from '@/router';
import { FileUploaderFactory } from '@/factories/file-uploader.factory';
import { ProcessorType, type SavingOptions } from '@/services/types/file-uploader.types';

export interface SectionsState {
  sections: SectionDto[];
  itemLoadingStates: Set<number>;
}

function insertOrUpdate<T>(array: T[], item: T, key: keyof T) {
  const index = array.findIndex((i) => i[key] === item[key]);
  if (index !== -1) {
    array[index] = item;
  } else {
    array.push(item);
  }
}

export const useSectionsStore = defineStore({
  id: 'sections',
  state: (): SectionsState => ({
    sections: [],
    itemLoadingStates: new Set<number>(),
  }),
  getters: {
    isItemLoading: (state) => (sectionId: number) => state.itemLoadingStates.has(sectionId),
    getItem: (state) => (sectionId: number) => state.sections.find((s) => s.id === sectionId),
    getSectionsByConsultationId: (state) => (consultationId: number) =>
      state.sections.filter((s) => s.consultation_id === consultationId),
    previewURL: (state) => (sectionId: number) => {
      const section = state.sections.find((s) => s.id === sectionId);
      const consultationStore = useConsultationsStore();
      const consultationUrl = consultationStore.previewURL(section.consultation_id);
      return `${consultationUrl}?section=${sectionId}`;
    },
  },
  actions: {
    async fetchSection(sectionId: number) {
      const consultationStore = useConsultationsStore();
      this.itemLoadingStates.add(sectionId);
      try {
        const { data } = await apiService.get<SectionDto>(`${Config.api.v2}/sections/${sectionId}`);
        insertOrUpdate(this.sections, data, 'id');
        await consultationStore.fetchConsultation(data.consultation_id);
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(sectionId);
      }
    },
    async fetchSectionsByConsultation(consultationId: number) {
      try {
        const { data } = await apiService.get<SectionDto[]>(
          `${Config.api.v2}/consultations/${consultationId}/sections`,
        );
        for (const section of data) {
          insertOrUpdate(this.sections, section, 'id');
        }
      } catch (error) {
        console.error(error);
      }
    },
    createSection(consultationId: number): SectionDto {
      return {
        consultation_id: consultationId,
        status: Constants.Section.Status.Active,
        title: { en: '', fr: '' },
        description: { en: '', fr: '' },
        order_num: -1,
        report_summary: null,
        options: {
          citizen_proposal: false,
          map: false,
          hide_results_comments: false,
        },
        contextOpened: false,
        attachmentsOpened: false,
      };
    },
    async saveSection(
      section: SectionDto,
      options: { isTemplate: boolean; consultationId?: number } = { isTemplate: false },
    ): Promise<boolean> {
      try {
        let sectionId = section.id;
        if (section.id) {
          await apiService.put<SectionDto>(`${Config.api.v2}/sections/${section.id}`, section);
        } else {
          const { data } = await apiService.post<SectionDto>(`${Config.api.v2}/sections`, section);
          sectionId = data.id;
          if (options.isTemplate) {
            router.push({
              name: 'section_template_details',
              params: { consultationId: options.consultationId, id: sectionId },
            });
          } else {
            router.push({ name: 'section_details', params: { id: sectionId } });
          }
        }
        await this.fetchSection(sectionId!);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async deleteSection(section: SectionDto) {
      try {
        await apiService.delete(`${Config.api.v2}/sections/${section.id}`);
        const index = this.sections.findIndex((s) => s.id === section.id);
        this.sections.splice(index, 1);
      } catch (error) {
        console.error(error);
      }
    },
    async saveSectionBanner(sectionId: number, options: SavingOptions) {
      try {
        await FileUploaderFactory.create(options.version).upload({
          image: options.image,
          method: apiService.post,
          processorType: ProcessorType.Multer,
          url: `${Config.api.v2}/sections/${sectionId}/banner`,
        });
        await this.fetchSection(sectionId);
      } catch (error) {
        console.error(error);
        // this.error = error.message;
      }
    },
    async clearSectionBanner(sectionId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/sections/${sectionId}/banner`);
        await this.fetchSection(sectionId);
      } catch (error) {
        console.error(error);
        // this.error = error.message;
      }
    },
  },
});
